import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../common/DataTable';
import DateTimeDisplay from '../common/DateTimeDisplay';
import TagRelationPicker from '../tags/TagRelationPicker';
import FilterFieldSelect from '../forms/FilterFieldSelect';
import FilterFieldCheck from '../forms/FilterFieldCheck';
import LabelPill from '../../../shared/components/pill/LabelPill';
import StatusPill from '../../../shared/components/pill/StatusPill';
import formatBytes from '../../../shared/utils/formatBytes';
import fileTypeLabel from '../../utils/fileType';
import { uploadStatus, knowledgeIndexStatus } from '../../utils/mediaStatus';

// A document is only reachable by a field engineer if it is shared *and* scoped — Global, or at
// least one tag. Shared with neither is the one combination that silently does nothing, so it is
// called out on the row and has a tab of its own.
function needsScoping(item) {
  return !!item.visible_to_field_engineers && (item.tag_ids || []).length === 0;
}

const SHARING_OPTIONS = [
  { value: 'all', label: 'All documents' },
  { value: 'shared', label: 'Shared' },
  { value: 'not_shared', label: 'Not shared' },
];

function aiPending(item) {
  return ['pending', 'processing'].includes(item.knowledge_index_status);
}

export default function DocumentLibraryTable({
  items,
  tags,
  onDelete,
  onDownload,
  onSetVisible,
  onRetryIndex,
  onSetTags,
}) {
  // Filtering is client-side here, unlike the assets and workflows lists, which push their
  // filters into the URL and refetch: the library arrives as one list and is small enough to
  // stay that way. The controls are the shared ones either way.
  const [sharing, setSharing] = useState('all');
  const [onlyNeedsScoping, setOnlyNeedsScoping] = useState(false);
  const [onlyAiPending, setOnlyAiPending] = useState(false);

  const tagNamesById = useMemo(
    () => Object.fromEntries(tags.map((t) => [t.id, t.name])),
    [tags],
  );

  const visible = useMemo(() => items.filter((item) => {
    if (sharing === 'shared' && !item.visible_to_field_engineers) return false;
    if (sharing === 'not_shared' && item.visible_to_field_engineers) return false;
    if (onlyNeedsScoping && !needsScoping(item)) return false;
    if (onlyAiPending && !aiPending(item)) return false;
    return true;
  }), [items, sharing, onlyNeedsScoping, onlyAiPending]);

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this document? It will be removed for all assets.')) return;
    try {
      await onDelete(item.id);
    } catch {
      // error surfaced via hook state
    }
  };

  const columns = [
    {
      key: 'filename',
      label: 'Document',
      sortable: true,
      render: (row) => {
        const badge = uploadStatus(row.status);
        return (
          <div className="d-flex align-items-center gap-2 min-w-0">
            <LabelPill tone="quiet" size="inline" className="flex-shrink-0 font-monospace">
              {fileTypeLabel(row.filename, row.content_type)}
            </LabelPill>
            <div className="min-w-0">
              <div className="fw-semibold text-truncate" title={row.filename}>{row.filename}</div>
              <div className="small text-muted">
                {formatBytes(row.byte_size)}
                {badge && (
                  <StatusPill tone={badge.tone} size="inline" className="ms-2">{badge.label}</StatusPill>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'knowledge_index_status',
      label: 'AI',
      sortable: true,
      render: (row) => {
        const state = knowledgeIndexStatus(row.knowledge_index_status);
        return (
          <StatusPill tone={state.tone} title={row.knowledge_index_error || undefined}>
            {state.label}
          </StatusPill>
        );
      },
    },
    {
      key: 'visible_to_field_engineers',
      label: 'Shared',
      sortable: true,
      filterValue: (row) => (row.visible_to_field_engineers ? 'shared' : 'not shared'),
      render: (row) => (
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`doc-fe-visible-${row.id}`}
            aria-label={`Share ${row.filename} with field engineers`}
            checked={!!row.visible_to_field_engineers}
            disabled={row.status !== 'ready'}
            onChange={(e) => onSetVisible(row.id, e.target.checked)}
          />
        </div>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      // Searching a tag name should find the documents scoped to it, which is what the
      // placeholder promises.
      filterValue: (row) => (row.tag_ids || []).map((id) => tagNamesById[id] || '').join(' '),
      render: (row) => (
        <div className="d-flex align-items-center flex-wrap gap-2">
          <TagRelationPicker
            tags={tags}
            selectedIds={row.tag_ids || []}
            pinGlobal
            disabled={row.status !== 'ready'}
            onChange={(tagIds) => onSetTags(row.id, tagIds)}
          />
          {needsScoping(row) && (
            <span className="text-warning-emphasis small text-nowrap">
              <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden />
              Not visible on any asset
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      sortable: true,
      className: 'text-nowrap',
      render: (row) => <DateTimeDisplay isoString={row.updated_at} />,
    },
  ];

  const actions = [
    {
      key: 'reindex',
      label: 'Re-index this document',
      icon: 'bi-arrow-clockwise',
      onClick: (row) => onRetryIndex(row.id, { ocr: false }),
      visible: (row) => row.status === 'ready' && row.visible_to_field_engineers,
    },
    {
      key: 'ocr',
      label: 'Re-index with OCR — reads text out of scanned pages and images. Much slower, but the only way to index a scanned document.',
      // No icon: OCR has no glyph anyone would read as OCR, and the eye it used to borrow
      // means "view" everywhere else in the admin.
      text: 'OCR',
      onClick: (row) => onRetryIndex(row.id, { ocr: true }),
      visible: (row) => row.status === 'ready' && row.visible_to_field_engineers,
    },
    {
      key: 'download',
      label: 'Download',
      icon: 'bi-download',
      onClick: (row) => onDownload(row.id),
      visible: (row) => row.status === 'ready',
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'bi-trash',
      tone: 'danger',
      onClick: handleDelete,
    },
  ];

  const filterExtras = (
    <>
      <FilterFieldSelect
        id="documents-filter-sharing"
        label="Sharing"
        value={sharing}
        onChange={(e) => setSharing(e.target.value)}
        options={SHARING_OPTIONS}
      />
      <FilterFieldCheck
        id="documents-filter-needs-scoping"
        label="Needs scoping"
        checked={onlyNeedsScoping}
        onChange={(e) => setOnlyNeedsScoping(e.target.checked)}
      />
      <FilterFieldCheck
        id="documents-filter-ai-pending"
        label="AI pending"
        checked={onlyAiPending}
        onChange={(e) => setOnlyAiPending(e.target.checked)}
      />
    </>
  );

  return (
    <DataTable
      items={visible}
      columns={columns}
      actions={actions}
      filterExtras={filterExtras}
      filterPlaceholder="Search documents or tags…"
      filterSummary={(shown, total) => `${shown} of ${total} document${total === 1 ? '' : 's'}`}
      emptyMessage="No documents here."
    />
  );
}

DocumentLibraryTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  tags: PropTypes.arrayOf(PropTypes.object),
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onSetVisible: PropTypes.func.isRequired,
  onRetryIndex: PropTypes.func.isRequired,
  onSetTags: PropTypes.func,
};

DocumentLibraryTable.defaultProps = {
  tags: [],
  onSetTags: () => {},
};
